<script setup lang="ts">

import { computed, reactive } from "vue";
import { ConditionType, type ICondition } from "@domain/Vehicle";
import { useUser } from "@domain/Security/User";
import { useAppConfiguration } from "@domain/AppConfiguration";
import { CallToActionType } from "@domain/General/Enums/CallToActionType";

import ConditionDetails from "./ConditionDetails.vue";
import { FeatureType } from "@domain/CarePlan/WorkOrders/FeatureType";

const model = defineModel<ICondition>( { required: true } );
const emit = defineEmits( [ 'show-flag-tip', 'mark-done' ] );

const showHelpButton = false;
const user = reactive( useUser()! );

// Get the configuration of the condition - a default (disabled) condition will be returned if the
// the app configuraiton is missing a configuration for the condition type
const appConfiguration = useAppConfiguration();
const configuration = appConfiguration.getConditionConfiguration( model.value.type );

const bookNowUrlLink = computed( () => {
    if( configuration?.offer?.hasLink)
    {
        return configuration.offer.linkUrl;
    }
    else if( configuration?.callToActionType === CallToActionType.Url)
    {
        return configuration.callToAction;
    }
    return null;
});

</script>

<template>

    <div class="w-full overflow-hidden
                flex flex-col group
                text-white/60">

        <!-- TAB / TITLE -->
        <div class="h-3 mr-auto pt-2 rounded-t-md 2xl:rounded-t-lg bg-gray-700">
            <div class="h-4 w-4 bg-gray-700 absolute -right-4 bottom-0">
                <div class="h-4 w-4 bg-gray-850 rounded-bl-[30%]" />
            </div>
            <h1 class="text-xs lg:text-base flex items-center gap-x-3 lg:gap-x-4 -left-1 z-10
                       text-primary-500 dark:text-white px-4 lg:px-6 py-1 tracking-widest">

                <Icon :name="model.icon" class="text-2xl" />
                <span class="inline-block uppercase 2xl:text-lg">
                    {{ model.labels.name }}
                </span>
            </h1>
        </div>

        <!-- CONTENT -->
        <div class="gap-2 justify-center items-center
                    bg-gradient-to-b from-gray-700 to-gray-800
                    rounded-b-lg 2xl:rounded-b-xl
                    pt-5 rounded-tr-lg 2xl:rounded-tr-xl
                    text-sm">

            <!-- HEADER ROW -->
            <div class="col-span-3 flex flex-col items-start justify-center"
                 style="filter: drop-shadow( 1px 1px 2px rgba( 0, 0, 0, 0.08) )">

                <!-- ICON TRAY, TOP-RIGHT -->
                <div class="flex gap-1 items-center self-end top-1 pr-3 -mt-2">

                    <!-- ALERTED FLAG -->
                    <!-- <IconButton v-if="model.hasAlert"
                                name="flag"
                                class="text-2xl drop-shadow-lg"
                                :class="{
                                    'text-amber-500 dark:text-amber-400': model.isDue,
                                    '!text-rose-500': model.isCritical || model.isOverdue || model.score <= 0.1,
                                }"
                                @click="() => emit( 'show-flag-tip', model )" /> -->

                    <!-- HELP / MORE INFORMATION -->
                    <IconButton v-if="showHelpButton"
                                name="help-me"
                                class="text-2xl -top-px"
                                :to="`/my-garage/more-info/${ ConditionType.getMoreInfoPage( model.type ) }`" />

                </div>

            </div>

            <!-- INFORMATION ABOUT THE CONDITION -->
            <div class="flex flex-col justify-start w-full h-full pt-4">

                <ConditionDetails v-model="model" />

                <slot></slot>
                
                <ContentSpacer :class="model.workOrderConfiguration == null ? 'mt-4' : ''"  />

                <!-- OPTIONS -->
                <div class="flex-1 flex justify-center items-center">

                    <!-- <RouterLink
                         v-if="model.workOrderConfiguration != null"
                         :to="`/my-garage/care-plan/item/${model.workOrderConfiguration?.type ?? '-'}/${configuration?.conditionType}`"
                         class="flex justify-center items-center gap-2 w-full rounded-b-md 2xl:rounded-b-lg py-4">

                        <div class="flex items-center justify-center w-full px-4 2xl:px-6">
                            <div class="tracking-wider text-white/80 text-sm border-r border-white/30 
                                pr-4 mr-4 2xl:pr-6 2xl:mr-6
                                mb-1">
                                <span>{{ model.labels.protectedByLabel }}</span>
                                <img v-if="model.workOrderConfiguration.product == FeatureType.Rewards"
                                     src="@/assets/images/rewards-logo.png"
                                     class="w-full mt-2 drop-shadow-md brightness-125 ml-auto" />
                                <img v-else-if="model.workOrderConfiguration.product == FeatureType.Protect"
                                     src="@/assets/images/protect-logo.png" 
                                     class="w-full mt-2 drop-shadow-md brightness-125 ml-auto" />
                            </div>
                            <div class="flex items-center text-sm gap-1 uppercase">
                                Manage
                                <Icon name="chevron-right" />
                            </div>
                        </div>

                    </RouterLink> -->

                    <div v-if="model.workOrderConfiguration != null"
                        class="flex justify-center items-center gap-2 w-full rounded-b-md 2xl:rounded-b-lg py-4">
                        <Button 
                            variant="text"
                            color="current"
                            class="rounded-[8px] !h-11 px-2 lg:px-3"
                            :to="`/my-garage/care-plan/item/${model.workOrderConfiguration?.type ?? '-'}/${configuration?.conditionType}?manage=true`">
                            <span v-if="model.isUnconfigured" class="text-[12px] lg:text-[14px]">Set Due Date</span>
                            <span v-else class="text-[12px] lg:text-[14px]">Manage</span>
                        </Button>
                    </div>
                        
                    <!-- <RouterLink
                         v-if="model.workOrderConfiguration != null"
                         :to="`/my-garage/care-plan/item/${model.workOrderConfiguration?.type ?? '-'}/${configuration?.conditionType}`"
                         class="flex justify-center items-center gap-2 w-full rounded-b-md 2xl:rounded-b-lg py-4">

                        <div class="flex items-center justify-center w-full px-4 2xl:px-6">
                            <div class="tracking-wider text-white/80 text-sm border-r border-white/30 
                                pr-4 mr-4 2xl:pr-6 2xl:mr-6
                                mb-1">
                                <span>{{ model.labels.protectedByLabel }}</span>
                                <img v-if="model.workOrderConfiguration.product == FeatureType.Rewards"
                                     src="@/assets/images/rewards-logo.png"
                                     class="w-full mt-2 drop-shadow-md brightness-125 ml-auto" />
                                <img v-else-if="model.workOrderConfiguration.product == FeatureType.Protect"
                                     src="@/assets/images/protect-logo.png" 
                                     class="w-full mt-2 drop-shadow-md brightness-125 ml-auto" />
                            </div>
                            <div class="flex items-center text-sm gap-1 uppercase">
                                Manage
                                <Icon name="chevron-right" />
                            </div>
                        </div>

                    </RouterLink> -->
                    
                    <div v-else
                        class="flex justify-center items-center gap-2 w-full rounded-b-md 2xl:rounded-b-lg py-4">
                    
                        <Button 
                            variant="text"
                            color="current"
                            class="rounded-[8px] !h-11 px-2 lg:px-3"
                            :to="`/my-garage/care-plan/item/-/${configuration?.conditionType}?manage=true`">
                            <span v-if="model.isUnconfigured" class="text-[12px] lg:text-[14px]">Set Due Date</span>
                            <span v-else class="text-[12px] lg:text-[14px]">Manage</span>
                        </Button>

                        <!-- <template v-if="configuration?.callToActionType == CallToActionType.PhoneNumber">
                            <JupiterButton :variant="isDarkMode ? 'outlined' : 'flat'"
                                            :color="isDarkMode ? 'current' : 'primary'"
                                            type="tel"
                                            class="rounded-[8px] !h-11 px-2 lg:px-3"
                                            :class="{ 'my-2': isDarkMode }"
                                            :to="configuration.callToAction">
                                <span class="text-[12px] lg:text-[14px]">Book now</span>
                            </JupiterButton>
                        </template> -->

                        <!-- BOOK APPOINTMENT -->
                        <!-- <template v-else-if="bookNowUrlLink">
                            <JupiterButton :variant="isDarkMode ? 'outlined' : 'flat'"
                                            :color="isDarkMode ? 'current' : 'primary'"
                                            type="url"
                                            class="rounded-[8px] !h-11 px-2 lg:px-3"
                                            :class="{ 'my-2': isDarkMode }"
                                            :to="bookNowUrlLink">
                                <span class="text-[12px] lg:text-[14px]">Book now</span>
                            </JupiterButton>
                        </template> -->

                        <!-- EXPLORE OFFERS -->
                        <!-- <template v-else-if="model.isUnconfigured == false && configuration?.hasCategory">
                            <JupiterButton :variant="isDarkMode ? 'outlined' : 'outlined'"
                                            :color="isDarkMode ? 'current' : 'primary'"
                                            type="page"
                                            to="marketplace"
                                            class="rounded-[8px] !h-11 px-2 lg:px-3"
                                            :class="{ 'my-2': isDarkMode }"
                                            :options="{ group: 'automotive', categoryId: configuration.categoryId }">
                                <span class="text-[12px] lg:text-[14px]">Explore Offers</span>
                            </JupiterButton>
                        </template> -->

                        <!--  CONFIGURE -->
                        <!-- <template v-if="model.isCritical == false && ( ( user.isExpert || model.hasAlert == false ) || ( ( model.type == ConditionType.Service || model.type == ConditionType.Rego ) && model.hasAlert )  )">
                            <Button class="flex items-center rounded-[8px] !h-11 !px-2 lg:!px-3"
                                    variant="text"
                                    :to="`/my-garage/configure-condition/${ model.type }`">
                                <Icon name="calendar"
                                        class="text-3xl opacity-70 !hidden sm:!block mr-1 lg:mr-2" />
                                <span class="text-[12px] lg:text-[14px]">
                                    {{ model.isUnconfigured ? 'Set' : 'Change' }} Due Date
                                </span>
                            </Button>
                        </template> -->

                    </div>

                    <!-- MARK DONE AND ADVANCE TO NEXT POSITION -->
                    <!-- <template v-if="model.configurationType != ConfigurationType.Date && configuration?.isNotLinkedToMarketplace && ( model.isDue || model.isOverdue || ( model.isCritical == false && model.isUnconfigured == false && user.isExpert ) )">
                        <div class="flex items-center justify-center py-4 2xl:py-6"
                                :class="{ '!pl-0': user.isExpert == false }">
                            <Button class="flex items-center rounded-[8px] !h-11 px-1 md:px-2 lg:px-3"
                                    variant="text"
                                    @click="() => emit( 'mark-done', model )">
                                <Icon name="progress-check"
                                        class="text-4xl opacity-70 mr-0.5 lg:mr-2" />
                                <span class="text-[12px] lg:text-[14px]">Mark Done</span>
                            </Button>
                        </div>
                    </template> -->

                </div>

            </div>

        </div>

        <div class="h-4 2xl:h-6 bg-gray-850 group-last:hidden" />

    </div>

</template>
